import { getResult, type ProgressData } from '../core/progress';
import type { LevelEntry, ZoneMeta } from '../game/registry';
import { sprite } from './sprites';

const THEME_TINT: Record<string, string> = {
  forest: '#5aa05a',
  desert: '#d9b066',
};

/** Rough, energetic halo ring (wavy radius) — drawn around the selected zone. */
function roughRing(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  base: number,
  amp: number,
  color: string,
  lw: number,
): void {
  const segments = 48;
  ctx.beginPath();
  for (let s = 0; s <= segments; s++) {
    const a = (s / segments) * Math.PI * 2;
    const r = base + Math.sin(a * 7) * amp + Math.cos(a * 3) * amp * 0.5;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (s === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.strokeStyle = color;
  ctx.lineWidth = lw;
  ctx.stroke();
}

/** Top-level zone picker: each zone as a themed disc; the selected one gets a rough glow. */
export function renderZoneSelect(
  ctx: CanvasRenderingContext2D,
  viewW: number,
  viewH: number,
  zones: ZoneMeta[],
  levelsByZone: (zoneId: string) => LevelEntry[],
  progress: ProgressData,
  selected: number,
  zoneUnlocked: (i: number) => boolean,
): void {
  const theme = zones[selected]?.theme ?? 'forest';
  const bg = sprite(`bg_${theme}`);
  if (bg) ctx.drawImage(bg, 0, 0, viewW, viewH);
  else {
    ctx.fillStyle = '#13162b';
    ctx.fillRect(0, 0, viewW, viewH);
  }
  ctx.fillStyle = 'rgba(15,15,27,0.5)';
  ctx.fillRect(0, 0, viewW, viewH);

  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.font = '14px monospace';
  ctx.fillText('LOKI — choose a zone', 16, 12);

  const n = zones.length;
  zones.forEach((z, i) => {
    const cx = ((i + 0.5) / n) * viewW;
    const cy = viewH * 0.5;
    const levels = levelsByZone(z.id);
    const done = levels.filter((l) => getResult(progress, l.id).completed).length;
    const unlocked = zoneUnlocked(i);
    const tint = THEME_TINT[z.theme] ?? '#8893b0';

    if (i === selected) {
      roughRing(ctx, cx, cy, 44, 5, 'rgba(255,255,255,0.5)', 2);
      roughRing(ctx, cx, cy, 54, 7, 'rgba(255,255,255,0.22)', 2);
    }

    ctx.beginPath();
    ctx.arc(cx, cy, 30, 0, Math.PI * 2);
    ctx.fillStyle = unlocked ? tint : '#3b4a6a';
    ctx.fill();
    if (i === selected) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
    }

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.font = '13px monospace';
    ctx.fillText(z.name, cx, cy + 44);
    ctx.font = '10px monospace';
    ctx.fillText(unlocked ? `${done}/${levels.length} cleared` : '🔒 locked', cx, cy + 62);
  });

  ctx.textAlign = 'left';
  ctx.font = '10px monospace';
  ctx.fillText('←/→ choose   Space enter', 16, viewH - 18);
}
